/**
 * Карточка закрытой сделки серии: направление, цены входа и выхода, время, экспирация, результат
 * (просьба заказчика 2026-09-18 — «кликать на закрытые сделки и смотреть, как они закрылись»).
 * У сделок симуляции цен и времени нет — показываем то, что есть.
 */
import type { TradeStep } from '../lib/api.js';
import { money } from '../lib/format.js';
import { SheetPortal } from './SheetPortal.js';

interface Props {
  step: TradeStep | null;
  t: (key: string, params?: Record<string, string | number>) => string;
  onClose: () => void;
}

function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function priceText(value: number | undefined, digits: number): string {
  return value === undefined ? '—' : value.toFixed(digits);
}

/** Знаков после запятой — как у самой цены (у JPY-пар 3, у остальных 5). */
function digitsOf(step: TradeStep): number {
  const sample = step.openPrice ?? step.closePrice ?? 0;
  const text = String(sample);
  const dot = text.indexOf('.');
  return dot === -1 ? 2 : Math.min(6, Math.max(2, text.length - dot - 1));
}

export function StepSheet({ step, t, onClose }: Props) {
  if (!step) return null;
  const digits = digitsOf(step);
  const hasPrices = step.openPrice !== undefined;
  const change =
    step.openPrice !== undefined && step.closePrice !== undefined
      ? step.closePrice - step.openPrice
      : undefined;
  const closedAt =
    step.closedAt ??
    (step.openedAt !== undefined && step.expirationSeconds ? step.openedAt + step.expirationSeconds * 1000 : undefined);

  return (
    <SheetPortal>
      <div
        className="sheet-backdrop"
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <div className="sheet step-sheet">
          <div className="sheet-handle" />
          <span className="kicker">{t('app.trade.step_details')}</span>
          <div className="step-sheet-head">
            <h2 className="card-title">{step.pairLabel ?? step.pair ?? t('app.trade.step', { index: step.index })}</h2>
            <span className={`pill ${step.won ? 'pill-green' : 'pill-pink'}`}>
              {t(step.won ? 'app.trade.step_won' : 'app.trade.step_lost')}
            </span>
          </div>

          <div className={`step-sheet-profit ${step.won ? 'is-win' : 'is-loss'}`}>
            {step.profit > 0 ? '+' : '−'}${money(Math.abs(step.profit))}
          </div>

          <dl className="step-sheet-grid">
            <dt>{t('app.trade.step_direction')}</dt>
            <dd className={step.action === 'call' ? 'is-up' : 'is-down'}>
              {step.action === 'call' ? '▲ ' : '▼ '}
              {t(step.action === 'call' ? 'app.terminal.up' : 'app.terminal.down')}
            </dd>

            <dt>{t('app.trade.step_amount')}</dt>
            <dd>${money(step.amount)}</dd>

            {hasPrices && (
              <>
                <dt>{t('app.trade.step_open_price')}</dt>
                <dd>{priceText(step.openPrice, digits)}</dd>
                <dt>{t('app.trade.step_close_price')}</dt>
                <dd>
                  {priceText(step.closePrice, digits)}
                  {change !== undefined && (
                    <span className={`step-sheet-delta ${change >= 0 ? 'is-up' : 'is-down'}`}>
                      {change >= 0 ? ' +' : ' '}
                      {change.toFixed(digits)}
                    </span>
                  )}
                </dd>
              </>
            )}

            {step.openedAt !== undefined && (
              <>
                <dt>{t('app.trade.step_opened')}</dt>
                <dd>{clock(step.openedAt)}</dd>
              </>
            )}
            {closedAt !== undefined && (
              <>
                <dt>{t('app.trade.step_closed')}</dt>
                <dd>{clock(closedAt)}</dd>
              </>
            )}
            {step.expirationSeconds !== undefined && (
              <>
                <dt>{t('app.trade.step_expiration')}</dt>
                <dd>{step.expirationSeconds}s</dd>
              </>
            )}

            <dt>{t('app.trade.step_running')}</dt>
            <dd>
              {step.runningProfit >= 0 ? '+' : '−'}${money(Math.abs(step.runningProfit))}
            </dd>

            {step.brokerTradeId && (
              <>
                <dt>{t('app.trade.step_trade_id')}</dt>
                <dd className="step-sheet-id">{step.brokerTradeId}</dd>
              </>
            )}
          </dl>

          {!hasPrices && <p className="body step-sheet-note">{t('app.trade.step_simulated')}</p>}

          <button type="button" className="cta cta-secondary cta-block" onClick={onClose}>
            {t('app.wallet.close')}
          </button>
        </div>
      </div>
    </SheetPortal>
  );
}
